import React from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { ChevronRight, ArrowRight } from 'lucide-react';
import CodeBlocks from './quads/CodeBlocks'
import Dither from '@/components/landing/common/Dither';
import Diff from './quads/Diff'
import Vid from './quads/Video'
import Windows from './quads/Windows'
import { breakpoints } from '@/layouts/breakpoints';


const StyledComp = styled.div`
  background: rgba(var(--whiter), 0.9);
  color: var(--black);
  padding: 96px 24px 64px;
  padding-bottom: 96px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  position: relative;
  .dbg{
    top: 0;
    left: 0;
    z-index: -1;
    width: 100vw;
    height: 100%;
    position: absolute;
  }

  .eyebrow {
    opacity: 0;
    display: inline-flex;
    align-items: center;
    gap: 10px;
    padding: 6px 16px 6px 6px;
    border-radius: 999px;
    background: rgba(var(--greyr), 0.08);
    border: 1px solid rgba(var(--greyr), 0.25);
    font-size: 13px;
    color: var(--grey);
    margin-bottom: 32px;
  }

  .eyebrow .new-badge {
    padding: 3px 10px;
    border-radius: 999px;
    border: 1px solid var(--purple);
    color: var(--purple);
    font-size: 11px;
    font-weight: 600;
  }

  h1 {
    font-size: clamp(40px, 6vw, 68px);
    line-height: 1.08;
    letter-spacing: -0.02em;
    margin: 0;
    max-width: 900px;
  }

  .subtitle {
    margin: 28px 0 0;
    font-size: 18px;
    line-height: 1.5;
    color: var(--grey);
    max-width: 620px;
  }

  .cta-row {
    display: flex;
    gap: 12px;
    margin-top: 36px;

    @media (max-width: ${breakpoints.small}) {
      flex-direction: column;
      .btn{
        text-align: center;
        justify-content: center;
      }
    }
  }

  .btn {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    padding: 12px 20px;
    border: none;
    border-radius: 8px;
    font-size: 16px;
    font-weight: 500;
    cursor: pointer;
    transition-duration: 0.3s;
  }

  .btn-primary {
    background-color: var(--black);
    color: var(--white);
  }

  .btn-primary:hover {
    background-color: #333;
  }

  .btn-secondary {
    background-color: transparent;
    color: var(--black);
  }

  .btn-secondary svg {
    padding-left: 10px;
    transition-duration: 0.3s;
  }

  .btn-secondary:hover {
    background-color: #eee;
  }

  .btn-secondary:hover svg {
    transform: translateX(2px);
  }

  .grid {
    margin-top: 80px;
    width: 100%;
    max-width: 1200px;
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 20px;
  }

  @media (max-width: 768px) {
    .grid {
      grid-template-columns: 1fr;
    }
  }

  .cell {
    position: relative;
    border: 1px solid rgba(var(--greyr), 0.2);
    border-radius: 16px;
    min-height: 320px;
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    padding: 20px 24px;
    text-align: left;
    overflow: hidden;
    transition-duration: 0.3s;
    background: white;
    &:hover{
      transition-duration: 0.3s;
      border-color: rgba(var(--greyr), 0.4);
      .cell-footer{
        opacity: 1;
        transition-duration: 0.3s;
        .cell-explore svg{
          transform: translateX(2px);
          transition-duration: 0.3s;
        }
      }
    }
  }

  .cell .area {
    position: absolute;
    inset: 0;
  }

  .cell .cell-footer {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: space-between;
    font-size: 15px;
  }

  .cell .cell-label {
    font-weight: 500;
    color: var(--black);
    &.white{
      color: var(--white);

    }
  }

  .cell-footer{
    opacity: 0.7;
    transition-duration: 0.3s;
  }

  .cell .cell-explore {
    color: var(--grey);
    font-size: 14px;
    display:flex;
    align-items: center;

    svg{
      transition-duration: 0.3s;
      padding-left: 4px;
    }
  }
`;

const shapes = ['square', 'circle', 'triangle', 'diamond']
const colors = ["#55efc4", "#00b894", "#81ecec", "#00cec9", "#74b9ff", "#0984e3", "#a29bfe", "#6c5ce7", "#fdcb6e", "#e17055", "#d63031", "#e84393", "#fd79a8", "#ff7675"]
const rand = <T,>(arr: T[]) => arr[Math.floor(Math.random() * arr.length)];


const Comp = ({isVisible}) => {
  const navigate = useNavigate();

  return (
    <StyledComp>
      <div className="dbg">
        <Dither
          shape={rand(shapes)}
          inkColor={rand(colors)}
          backgroundColor="#fff"
          pixelSize={Math.floor(Math.random() * 7) + 4} // 2-10
        />
      </div>

      <div className="eyebrow">
        <span className="new-badge">Beta</span>
        AxionQuant Studio
        <span>&rsaquo;</span>
      </div>

      <h1 className="serif">
        Financial Data API
        <br />
        Built for Scale
      </h1>

      <p className="subtitle">
        Historical market data, fundamental data, and quantitative research unified through a single financial data API.
      </p>

      <div className="cta-row">
        <button className="btn btn-primary" onClick={() => navigate('/login')}>
          Get API Access
        </button>
        <button className="btn btn-secondary" onClick={() => navigate('/docs')}>View Documentation <ChevronRight size={18} /></button>
      </div>

      <div className="grid">
        <div className="cell" onClick={() => navigate('/stocks')} style={{cursor: 'pointer'}}>
          <div className="area" >
            <CodeBlocks/>
          </div>

          <div className="cell-footer">
            <span className="cell-label">Analysis</span>
            <span className="cell-explore">Explore <ArrowRight width={16}/> </span>
          </div>
        </div>

        <div className="cell" onClick={() => navigate('/developers')} style={{cursor: 'pointer'}}>
          <div className="area" >
            <Diff/>
          </div>
          <div className="cell-footer">
            <span className="cell-label">Research</span>
            <span className="cell-explore">Explore <ArrowRight width={16}/> </span>
          </div>
        </div>

        <div className="cell" onClick={() => navigate('/dashboard/playground')} style={{cursor: 'pointer'}}>
          <div className="area" >
            {isVisible && <Vid/> }
          </div>

          <div className="cell-footer">
            <span className="cell-label white">Training</span>
            <span className="cell-explore">Explore <ArrowRight width={16}/></span>
          </div>
        </div>

        <div className="cell" onClick={() => navigate('/docs')} style={{cursor: 'pointer'}}>
          <div className="area">
            <Windows/>
          </div>
          <div className="cell-footer">
            <span className="cell-label">Visualizing</span>
            <span className="cell-explore">Explore <ArrowRight width={16}/></span>
          </div>
        </div>

      </div>
    </StyledComp>
  );
};

export default Comp;
